/**
 * Central registry for AI News Hub articles.
 * To add a new article: add metadata here and create the matching component in src/components/news/.
 *
 * Optional: cardImage (public URL), cardImageAlt, cardImageVariant ('editorial' | 'logo'),
 */

export const NEWS_CATEGORIES = {
  MILESTONE: 'Milestone',
  RESEARCH: 'Research',
  HARDWARE: 'Hardware',
  GOVERNANCE: 'Governance',
  OPEN_SOURCE: 'Open Source',
  SECURITY: 'Security',
};

export const NEWS_ARTICLES = [
  {
    slug: 'claude-fable-5-shutdown-2026',
    title: 'Claude Fable 5: Anthropic\'s Biggest Launch — Pulled in Three Days',
    date: '2026-06-12',
    displayDate: 'June 12, 2026',
    category: NEWS_CATEGORIES.MILESTONE,
    tags: ['Anthropic', 'Claude Fable 5', 'Claude Mythos 5', 'Frontier AI', 'Export Controls', 'Enterprise AI'],
    excerpt:
      'Anthropic launched Claude Fable 5 on June 9 — its most capable public Mythos-class model — then suspended Fable 5 and Mythos 5 for all users on June 12 after a US government export-control directive.',
    cardImage: '/Images/News/claude-logo-card.png',
    cardImageVariant: 'editorial',
    cardImageAlt: 'Claude logo on a cream editorial card for the Fable 5 launch and access suspension.',
    sources: [
      { label: 'Anthropic: Fable 5 & Mythos 5 launch', url: 'https://www.anthropic.com/news/claude-fable-5-mythos-5' },
      { label: 'Anthropic: Access suspension statement', url: 'https://www.anthropic.com/news/fable-mythos-access' },
      { label: 'Claude API docs: Introducing Fable 5', url: 'https://platform.claude.com/docs/en/about-claude/models/introducing-claude-fable-5-and-claude-mythos-5' },
      { label: 'AWS: Claude Fable 5 on Bedrock', url: 'https://aws.amazon.com/about-aws/whats-new/2026/06/claude-fable-5-aws/' },
      { label: 'IBM Think: Fable 5 launch', url: 'https://www.ibm.com/think/news/anthropic-launches-powerful-ai-model-fable-5-mythos-5-safety-guardrails' },
    ],
  },
  {
    slug: 'hanyuan-02-dual-core-quantum-2026',
    title: 'Hanyuan 02: A 200-Qubit Dual-Core Neutral-Atom Quantum Computer',
    date: '2026-05-08',
    displayDate: 'May 8, 2026',
    category: NEWS_CATEGORIES.HARDWARE,
    tags: [
      'Quantum Computing',
      'China',
      'Neutral Atom',
      'CAS Cold Atom',
      'Hanyuan 02',
      'Energy Efficiency',
    ],
    excerpt:
      'China unveiled Hanyuan 02, described as the first dual-core neutral-atom quantum processor, with 200 qubits (100 ⁸⁷Rb + 100 ⁸⁵Rb), sub-7 kW enclosure power, and lab-friendly deployment. Public benchmarks on real AI training or inference jobs are still thin; if the numbers hold up, it could still matter for optimization, simulation, and future quantum-classical pipelines.',
    cardImage: '/Images/News/hanyuan02.png',
    cardImageAlt:
      'Hanyuan 02 dual-core neutral-atom quantum computer by CAS Cold Atom, with China Mobile Cloud WuYueOS Inside branding, on display.',
    sources: [
      { label: 'IT之家 (summary via CCTV / domestic coverage)', url: 'https://www.ithome.com/0/947/905.htm' },
      { label: '新华网: quantum & Beijing fair roundup', url: 'https://www.news.cn/tech/20260514/c6ca7605aced452785c3e45974a0c68f/c.html' },
      { label: '国科网 (NCSTI): Hanyuan 02 briefing', url: 'https://www.ncsti.gov.cn/kjdt/kjrd/qtrd_kjrd/202605/t20260508_246046.html' },
    ],
  },
  {
    slug: 'deepseek-v4-preview-2026',
    title: 'DeepSeek V4 Preview: Open MoE Models at 1M Context',
    date: '2026-04-24',
    displayDate: 'April 24, 2026',
    category: NEWS_CATEGORIES.OPEN_SOURCE,
    tags: ['DeepSeek', 'V4', 'MoE', 'Open Source', 'Long Context', 'API', 'Agentic AI'],
    excerpt:
      'DeepSeek shipped V4 as a preview: two open-weight MoE lines (V4-Pro at 1.6T total / 49B active and V4-Flash at 284B / 13B), a one-million-token context window, API access, and weights on Hugging Face with a technical report.',
    cardImage: '/Images/News/deepseek-v4-card.png',
    cardImageAlt: 'DeepSeek wordmark on a dark background, editorial card for V4 open MoE at 1M context.',
    sources: [
      { label: 'DeepSeek API Docs: V4 Preview announcement', url: 'https://api-docs.deepseek.com/news/news260424' },
      { label: 'Hugging Face: DeepSeek V4 collection', url: 'https://huggingface.co/collections/deepseek-ai/deepseek-v4' },
      { label: 'DeepSeek V4 technical report (PDF on HF)', url: 'https://huggingface.co/deepseek-ai/DeepSeek-V4-Pro/blob/main/DeepSeek_V4.pdf' },
      { label: 'DeepSeek: Thinking mode guide', url: 'https://api-docs.deepseek.com/guides/thinking_mode' },
    ],
  },
  {
    slug: 'openai-gpt-5-5-launch-2026',
    title: 'OpenAI GPT-5.5: The Next Jump in Agentic, Token-Efficient Frontier Models',
    date: '2026-04-23',
    displayDate: 'April 23, 2026',
    category: NEWS_CATEGORIES.MILESTONE,
    tags: ['OpenAI', 'GPT-5.5', 'ChatGPT', 'API', 'Codex', 'Agentic AI', 'Benchmarks'],
    excerpt:
      'OpenAI launched GPT-5.5 and GPT-5.5 Pro on April 23, 2026, stressing stronger agentic coding and research, better token efficiency than prior generations, and broad rollout across ChatGPT, the API, and Codex with a full system card.',
    cardImage: '/Images/News/openai-gpt-5-5-card.png',
    cardImageAlt: 'OpenAI wordmark on a dark background, editorial card for GPT-5.5 (logo via Wikimedia Commons).',
    sources: [
      { label: 'OpenAI: Introducing GPT-5.5', url: 'https://openai.com/index/introducing-gpt-5-5/' },
      { label: 'OpenAI: GPT-5.5 System Card', url: 'https://openai.com/index/gpt-5-5-system-card/' },
      { label: 'TechCrunch', url: 'https://techcrunch.com/2026/04/23/openai-chatgpt-gpt-5-5-ai-model-superapp/' },
      { label: 'CNBC', url: 'https://www.cnbc.com/2026/04/23/openai-announces-latest-artificial-intelligence-model.html' },
    ],
  },
  {
    slug: 'hermes-agent-nous-research-2026',
    title: "Hermes Agent: Nous Research's Open, Self-Improving Agent Runtime",
    date: '2026-04-18',
    displayDate: 'April 2026',
    category: NEWS_CATEGORIES.OPEN_SOURCE,
    tags: ['Nous Research', 'Hermes Agent', 'Persistent Memory', 'Agents', 'Open Source', 'Automation'],
    excerpt:
      'Hermes Agent from Nous Research is an open-source autonomous agent framework built for durable memory, multi-channel messaging, optional reflective skill learning, and conservative security defaults - positioning it against ecosystem-heavy agent kits.',
    sources: [
      { label: 'Hermes Agent (Nous Research)', url: 'https://hermes-agent.nousresearch.com/' },
      { label: 'The New Stack - Persistent agents compared', url: 'https://thenewstack.io/persistent-ai-agents-compared/' },
      { label: 'I-Scoop overview', url: 'https://www.i-scoop.eu/hermes-agent-from-nous-research/' },
    ],
  },
  {
    slug: 'happy-horse-alibaba-video-2026',
    title: "Happy Horse 1.0: Alibaba's Leaderboard-Topping AI Video Model",
    date: '2026-04-10',
    displayDate: 'April 2026',
    category: NEWS_CATEGORIES.RESEARCH,
    tags: ['Alibaba', 'Happy Horse', 'AI Video', 'Text-to-Video', 'Artificial Analysis', 'ATH AI'],
    excerpt:
      'HappyHorse-1.0 - claimed by Alibaba’s ATH AI unit - reached #1 on the Artificial Analysis Video Arena in blind rankings, with unified audio-video generation and cinematic short clips that challenged Seedance, Kling, and Western rivals.',
    sources: [
      { label: 'CNBC - Alibaba reveal', url: 'https://www.cnbc.com/2026/04/10/alibaba-happyhorse-ai-video-model-benchmark-reveal.html' },
      { label: 'Yahoo Finance', url: 'https://sg.finance.yahoo.com/news/alibaba-claims-viral-happy-horse-004628524.html' },
      { label: 'fal.ai - Technical roundup', url: 'https://fal.ai/learn/devs/happyhorse-1-0-what-do-we-know-so-far' },
      { label: 'Forbes - HappyHorse context', url: 'https://www.forbes.com/sites/johnwerner/2026/04/13/happyhorse-a-kids-toy-or-ai-model/' },
    ],
  },
  {
    slug: 'anthropic-mythos-preview-2026',
    title: 'The AI That Found a 27-Year-Old Bug in Minutes - and Scared the Whole Industry',
    date: '2026-04-08',
    displayDate: 'April 2026',
    category: NEWS_CATEGORIES.SECURITY,
    tags: ['Anthropic', 'Claude Mythos', 'Zero-Day', 'Linux', 'OpenBSD', 'Cybersecurity', 'Project Glasswing'],
    excerpt:
      "Anthropic's Claude Mythos Preview discovered a 27-year-old OpenBSD vulnerability and thousands of zero-days across every major OS and browser - in minutes. The findings were so serious that Anthropic withheld the model from public release and convened tech's biggest names to respond.",
    sources: [
      { label: 'Anthropic Project Glasswing', url: 'https://www.anthropic.com/glasswing' },
      { label: 'Claude Mythos Preview', url: 'https://red.anthropic.com/2026/mythos-preview/' },
      { label: 'The Hacker News', url: 'https://thehackernews.com/2026/04/anthropics-claude-mythos-finds.html' },
      { label: 'Help Net Security', url: 'https://www.helpnetsecurity.com/2026/04/08/anthropic-claude-mythos-preview-identify-vulnerabilities/' },
      { label: 'NBC News', url: 'https://www.nbcnews.com/tech/security/anthropic-project-glasswing-mythos-preview-claude-gets-limited-release-rcna267234' },
      { label: 'Reddit r/openbsd', url: 'https://www.reddit.com/r/openbsd/comments/1sf588l/claude_mythos_preview_anthropic_finds_27_year_old/' },
    ],
  },
  {
    slug: 'gemma-4-google-edge-ai-2026',
    title: "Gemma 4 Is Here - Google's Open AI That Runs on Your Phone and Your Server",
    date: '2026-04-02',
    displayDate: 'April 2, 2026',
    category: NEWS_CATEGORIES.OPEN_SOURCE,
    tags: ['Google', 'Gemma 4', 'Edge AI', 'Open Source', 'MoE', 'Apache 2.0', 'Quantization'],
    excerpt:
      'Google launched Gemma 4 on April 2, 2026 - a family of open models from E2B (runs on a Raspberry Pi) to 31B, with a Mixture-of-Experts design that activates just 3.8B parameters per token. Apache 2.0 licensed, multimodal, and built for both edge devices and developer workstations.',
    sources: [
      { label: 'Google Blog', url: 'https://blog.google/innovation-and-ai/technology/developers-tools/gemma-4/' },
      { label: 'Google AI Model Card', url: 'https://ai.google.dev/gemma/docs/core/model_card_4' },
      { label: 'CIO Dive', url: 'https://www.ciodive.com/news/google-gemma-4-AI-model-open-source/816644/' },
      { label: 'MindStudio – MoE Explained', url: 'https://www.mindstudio.ai/blog/gemma-4-mixture-of-experts-architecture/' },
      { label: 'HuggingFace', url: 'https://huggingface.co/blog/gemma4' },
    ],
  },
  {
    slug: 'claude-military-autonomous-weapons-2026',
    title: 'Claude in the War Room: Anthropic\'s Stand Against Autonomous Weapons',
    date: '2026-03-10',
    displayDate: 'March 2026',
    category: NEWS_CATEGORIES.GOVERNANCE,
    tags: ['Anthropic', 'Claude', 'Military AI', 'Autonomous Weapons', 'Pentagon', 'Human-in-the-Loop', 'Trump'],
    excerpt:
      "Anthropic refused Pentagon demands to remove safety guardrails on Claude - triggering a supply-chain-risk designation, a 180-day removal order, and a lawsuit. Yet Claude was simultaneously being used in live military operations against Iran. The AI industry's first major test of holding safety lines against state-level pressure.",
    sources: [
      { label: 'The Verge', url: 'https://www.theverge.com/ai-artificial-intelligence/885773/anthropic-department-of-defense-dod-pentagon-refusal-terms-hegseth-dario-amodei' },
      { label: 'AP News', url: 'https://apnews.com/article/b2bbcf5fda3f27353eae1e0eb7ab07b6' },
      { label: 'Reuters', url: 'https://www.reuters.com/technology/pentagon-informed-anthropic-it-is-supply-chain-risk-official-says-2026-03-05/' },
      { label: 'Anthropic Statement', url: 'https://www.anthropic.com/news/statement-comments-secretary-war' },
      { label: 'Tom\'s Hardware', url: 'https://www.tomshardware.com/tech-industry/artificial-intelligence/claude-wont-be-allowed-to-engage-in-mass-surveillance-or-power-fully-autonomous-weapons-anthropic-refuses-to-lower-ai-guardrails-for-the-pentagon' },
    ],
  },
  {
    slug: 'seedance-2-bytedance-video-2026',
    title: 'Seedance 2.0: ByteDance\'s #1-Ranked AI Video Generator',
    date: '2026-03-25',
    displayDate: 'March 2026',
    category: NEWS_CATEGORIES.RESEARCH,
    tags: ['ByteDance', 'Seedance', 'AI Video', 'Multimodal', 'Physics AI'],
    excerpt:
      "ByteDance's Seedance 2.0 tops Artificial Analysis rankings above Google Veo 3, OpenAI Sora, and Runway Gen4 - generating 4K physics-correct video with audio sync, character consistency, and cinematic camera control in under 60 seconds.",
    sources: [
      { label: 'Nerdbot', url: 'https://nerdbot.com/2026/03/25/seedance-2-0-ai-video-generator-redefining-ai-video-creation/' },
      { label: 'Seedance 2', url: 'https://seedance2.dance/' },
      { label: 'Digital Journal', url: 'https://www.digitaljournal.com/pr/news/winston-news-wire/bigmotion-ai-offers-seedance-2-0-pro-1194822419.html' },
    ],
  },
  {
    slug: 'humanoid-robotics-unitree-nvidia-groot-2026',
    title: 'Humanoid Robots in 2026: Unitree, Boston Dynamics, Tesla & NVIDIA GR00T',
    date: '2026-01-06',
    displayDate: '2025 – 2026',
    category: NEWS_CATEGORIES.HARDWARE,
    tags: ['Robotics', 'Unitree', 'Boston Dynamics', 'Tesla Optimus', 'NVIDIA', 'GR00T', 'Isaac'],
    excerpt:
      "Unitree G1 at $13,500, Boston Dynamics' 56-DOF electric Atlas, Tesla Optimus Gen 3, and Figure 03 are racing to deploy humanoids. NVIDIA's open-source GR00T N1.6 gives every robot maker a trained foundation model - backed by Isaac Lab, Newton physics, and Omniverse NuRec.",
    sources: [
      { label: 'NVIDIA GR00T Newsroom', url: 'https://nvidianews.nvidia.com/news/nvidia-isaac-gr00t-n1-open-humanoid-robot-foundation-model-simulation-frameworks/' },
      { label: 'GR00T N1.6 Research', url: 'https://research.nvidia.com/labs/gear/gr00t-n1_6/' },
      { label: 'Unitree G1', url: 'https://unitree.com/g1/' },
      { label: 'VFuture Media', url: 'https://vfuturemedia.com/future-tech/humanoid-robots-enter-the-workforce-figure-boston-dynamics-and-tesla-optimus-2026/' },
    ],
  },
  {
    slug: 'nvidia-blackwell-architecture-2025',
    title: 'NVIDIA Blackwell: 208B Transistors, 30× LLM Inference, Confidential Computing',
    date: '2024-11-01',
    displayDate: 'Q4 2024 – 2025',
    category: NEWS_CATEGORIES.HARDWARE,
    tags: ['NVIDIA', 'Blackwell', 'B200', 'GB200', 'NVLink', 'HBM3E', 'GPU'],
    excerpt:
      "NVIDIA's Blackwell architecture - dual-die 208B transistors, FP4 Tensor Cores, fifth-gen NVLink scaling to 576 GPUs - delivers 30× faster LLM inference than H100 and introduces hardware confidential computing for regulated AI workloads.",
    sources: [
      { label: 'NVIDIA Blackwell Architecture', url: 'https://nvidia.com/en-gb/data-center/technologies/blackwell-architecture' },
      { label: 'Paul S Cannon Review', url: 'https://paulscannon.com/nvidia-blackwell-b200-specs-price-performance-review' },
      { label: 'Wikipedia Blackwell', url: 'https://en.wikipedia.org/wiki/Blackwell_(microarchitecture)' },
    ],
  },
  {
    slug: 'digital-twin-nvidia-omniverse-2026',
    title: 'Digital Twins in 2026: NVIDIA Omniverse & the Living Industrial World',
    date: '2026-03-01',
    displayDate: '2025 – 2026',
    category: NEWS_CATEGORIES.RESEARCH,
    tags: ['Digital Twin', 'NVIDIA', 'Omniverse', 'OpenUSD', 'AVEVA', 'Industrial AI'],
    excerpt:
      "NVIDIA Omniverse's OpenUSD-based digital twin platform is now the AI factory, automotive, and pharma standard - AVEVA integrated process simulation in March 2026, while the DSX Blueprint creates unified real-time simulations of entire data centres.",
    sources: [
      { label: 'NVIDIA Omniverse', url: 'https://www.nvidia.com/en-us/omniverse' },
      { label: 'AVEVA Press Release', url: 'https://www.aveva.com/en/about/news/press-releases/2026/aveva-develops-a-new-lifecycle-digital-twin-architecture-that-delivers-industrial-intelligence-for-gigawatt-scale-ai-factories-accelerated-by-nvidia/' },
      { label: 'NVIDIA Blog', url: 'https://blogs.nvidia.com/blog/industrial-ai-digital-twins-omniverse/' },
    ],
  },
  {
    slug: 'ai-predictive-analytics-enterprise-2026',
    title: 'AI Predictive Analytics: From Dashboards to Autonomous Decision Agents',
    date: '2026-03-24',
    displayDate: '2025 – 2026',
    category: NEWS_CATEGORIES.RESEARCH,
    tags: ['Predictive Analytics', 'Enterprise AI', 'SAP', 'Oracle', 'Anaplan', 'Agentic AI'],
    excerpt:
      "SAP RPT-1 delivers 3.5× better predictions at 50,000× less energy than LLMs. Oracle and Confluent enable real-time AI agent access to live operational data. IDC: 45% of organisations will orchestrate agents at scale by 2030 - or face 15% productivity loss from poor data quality.",
    sources: [
      { label: 'IDC FutureScape 2026', url: 'https://my.idc.com/getdoc.jsp?containerId=prUS53883425' },
      { label: 'SAP Business AI', url: 'https://news.sap.com/2026/01/sap-business-ai-release-highlights-q4-2025/' },
      { label: 'Oracle AI Database', url: 'http://www.oracle.com/news/announcement/oracle-unveils-ai-database-agentic-innovations-for-business-data-2026-03-24/' },
      { label: 'Anaplan AI', url: 'http://www.anaplan.com/news/anaplan-announces-latest-ai-driven-innovations-applications-to-advance-enterprise-decision-making/' },
    ],
  },
  {
    slug: 'llama-4-open-source-wave-2025',
    title: 'Llama 4 & the Open-Source Model Wave That Closed the Frontier Gap',
    date: '2025-04-05',
    displayDate: 'April 2025',
    category: NEWS_CATEGORIES.OPEN_SOURCE,
    tags: ['Meta', 'Llama 4', 'Open Source', 'MoE', 'Multimodal', 'HuggingFace'],
    excerpt:
      "Meta's Llama 4 Maverick (17B active / 128 experts) matches GPT-4o on a 10M-token context. Combined with Qwen3.5, Mistral Small 4, Kimi K2.5, and DeepSeek R1, the open-source frontier has closed to within months of proprietary releases - permanently reshaping enterprise AI strategy.",
    sources: [
      { label: 'Meta Llama 4 Blog', url: 'https://ai.meta.com/blog/llama-4-multimodal-intelligence' },
      { label: 'Llama 4 Models', url: 'http://llama.meta.com/models/llama-4/' },
      { label: 'HuggingFace Maverick', url: 'http://www.huggingface.co/meta-llama/Llama-4-Maverick-17B-128E' },
    ],
  },
  {
    slug: 'kimi-k2-5-moonshot-ai-2026',
    title: 'Kimi K2.5: 100 Parallel Agents, 1 Trillion Parameters, Open Source',
    date: '2026-01-27',
    displayDate: 'January 27, 2026',
    category: NEWS_CATEGORIES.RESEARCH,
    tags: ['Kimi', 'Moonshot AI', 'MoE', 'Agent Swarm', 'PARL', 'Open Source', 'Multimodal'],
    excerpt:
      "Moonshot AI's Kimi K2.5 brings 1T-parameter MoE reasoning, native video understanding, and a trained Agent Swarm orchestrating up to 100 parallel sub-agents with 1,500 simultaneous tool calls - open weights, 27% cheaper than GPT-5.2.",
    sources: [
      { label: 'GitHub MoonshotAI', url: 'https://github.com/MoonshotAI/Kimi-K2.5' },
      { label: 'Kimi K2.5 Release', url: 'https://kimi-k2.org/en/blog/20-kimi-k2-5-en' },
      { label: 'Spectrum AI Lab', url: 'https://spectrumailab.com/blog/kimi-k2-5-agent-swarm-moonshot-ai-guide-2026' },
    ],
  },
  {
    slug: 'parallel-agents-architecture-2026',
    title: 'Parallel Agents: The Architecture Reshaping How AI Thinks and Works',
    date: '2026-03-10',
    displayDate: 'March 2026',
    category: NEWS_CATEGORIES.RESEARCH,
    tags: ['Parallel Agents', 'Multi-Agent', 'Agentic AI', 'Anthropic', 'Google', 'PARL', 'ROMA'],
    excerpt:
      "Parallel agent systems - where multiple AI instances work simultaneously, debate results, and synthesise answers - delivered 90% performance gains in Anthropic's production deployment and 4.5× speedups in Kimi K2.5. But Google's research shows they degrade performance on sequential tasks by up to 70%.",
    sources: [
      { label: 'Implicator AI (Anthropic)', url: 'https://www.implicator.ai/inside-claudes-research-engine-are-teams-of-ai-agents-the-future-of-artificial-intelligence/' },
      { label: 'Google Research', url: 'https://research.google/blog/towards-a-science-of-scaling-agent-systems-when-and-why-agent-systems-work' },
      { label: 'Zylos Research', url: 'https://zylos.ai/research/2026-03-10-ai-agent-fork-merge-patterns' },
      { label: 'NVIDIA ProRL', url: 'https://www.marktechpost.com/2026/03/27/nvidia-ai-unveils-prorl-agent-a-decoupled-rollout-as-a-service-infrastructure-for-reinforcement-learning-of-multi-turn-llm-agents-at-scale/' },
    ],
  },
  {
    slug: 'vietnam-ai-law-sea-first-2026',
    title: "Vietnam's AI Law: Southeast Asia's First - Effective March 1, 2026",
    date: '2026-03-01',
    displayDate: 'March 1, 2026',
    category: NEWS_CATEGORIES.GOVERNANCE,
    tags: ['Vietnam', 'AI Law', 'SEA', 'Regulation', 'Deepfake', 'Content Labelling'],
    excerpt:
      "Vietnam became the first country in Southeast Asia to enact comprehensive binding AI legislation - requiring AI-generated content labelling, human agent disclosure, and human oversight for high-risk AI systems.",
    sources: [
      { label: 'Seasia.co', url: 'https://seasia.co/2026/03/01/first-in-southeast-asia-viet-nams-ai-law-takes-effect-on-march-1' },
      { label: 'Tribune Philippines', url: 'https://tribune.net.ph/2026/03/01/vietnam-ai-law-takes-effect-first-in-southeast-asia' },
      { label: 'Regulations.AI', url: 'https://regulations.ai/regulations/RAI-PH-NA-SUMMARY-2026' },
    ],
  },
  {
    slug: 'singapore-agentic-ai-governance-2026',
    title: "Singapore's World-First Agentic AI Governance Framework",
    date: '2026-01-15',
    displayDate: 'January 2026',
    category: NEWS_CATEGORIES.GOVERNANCE,
    tags: ['Singapore', 'Agentic AI', 'IMDA', 'AI Verify', 'Governance'],
    excerpt:
      "IMDA launched the world's first governance framework for agentic AI in January 2026 - covering upfront risk bounding, human accountability checkpoints, technical controls, and end-user transparency. Paired with the AI Assurance Sandbox and PETs guide.",
    sources: [
      { label: 'IMDA Press Release', url: 'https://www.imda.gov.sg/resources/press-releases-factsheets-and-speeches/press-releases/2026/new-model-ai-governance-framework-for-agentic-ai' },
      { label: 'PDPC Tools Launch', url: 'https://www.pdpc.gov.sg/news-and-events/press-room/2025/07/singapore-launches-new-tools-to-help-businesses-protect-data-and-deploy-ai-in-a-trusted-ecosystem' },
      { label: 'IMDA AI Overview', url: 'https://imda.gov.sg/about-imda/emerging-technologies-and-research/artificial-intelligence' },
    ],
  },
  {
    slug: 'asean-ai-governance-framework-2025',
    title: 'ASEAN AI Governance: From Voluntary Principles to Binding DEFA',
    date: '2025-03-01',
    displayDate: 'January 2025 – End 2026',
    category: NEWS_CATEGORIES.GOVERNANCE,
    tags: ['ASEAN', 'AI Governance', 'DEFA', 'GenAI', 'Policy', 'Southeast Asia'],
    excerpt:
      "ASEAN's layered AI governance architecture - from the 2025 Generative AI guide to the Responsible AI Roadmap 2025–2030 - is building toward a binding Digital Economy Framework Agreement (DEFA) by end 2026 that will create the region's first enforceable AI commitments.",
    sources: [
      { label: 'ASEAN Gen AI Guide', url: 'https://asean.org/book/expanded-asean-guide-on-ai-governance-and-ethics-generative-ai/' },
      { label: 'ISEAS Perspective', url: 'http://www.iseas.edu.sg/articles-commentaries/iseas-perspective/2025-13-what-is-shaping-artificial-intelligence-ai-governance-policies-in-southeast-asia-by-kristina-fong/' },
      { label: 'US-ASEAN Business Council', url: 'https://www.usasean.org/article/ai-governance-southeast-asia-2025' },
      { label: 'Pertama Partners', url: 'https://www.pertamapartners.com/insights/asean-ai-governance-ethics-guide-southeast-asia' },
    ],
  },
  {
    slug: 'malaysia-indonesia-sea-ai-governance',
    title: 'Malaysia, Indonesia, Philippines & Thailand: AI Governance in Progress',
    date: '2025-09-01',
    displayDate: '2025 – 2026',
    category: NEWS_CATEGORIES.GOVERNANCE,
    tags: ['Malaysia', 'Indonesia', 'Philippines', 'Thailand', 'AI Governance', 'NAIO', 'UU PDP'],
    excerpt:
      "Four of ASEAN's largest economies are building their AI governance frameworks simultaneously: Malaysia's NAIO and AIGE, Indonesia's dual Presidential Regulations, Philippines' NAIS-PH strategy, and Thailand's draft AI Law and AIGC centre.",
    sources: [
      { label: 'Malaysia AIGE', url: 'https://www.malaysia.gov.my/en/my-initiative/whole-government-digital-services/tadbir-urus/national-guidelines-on-artificial-intelligence-governance-and-ethics-aige' },
      { label: 'Indonesia AI 2026', url: 'https://www.pertamapartners.com/insights/indonesia-ai-regulations-2026' },
      { label: 'AMD Press', url: 'https://batamnewsasia.com/2026/03/18/indonesia-prepares-ai-presidential-regulation-to-balance-innovation-and-accountability/' },
      { label: 'Thailand AI Gov', url: 'https://law.asia/thailand-ai-governance-policy-framework/' },
      { label: 'Philippines NAIS-PH', url: 'https://regulations.ai/regulations/RAI-PH-NA-SUMMARY-2026' },
    ],
  },
  {
    slug: 'openclaw-clawd-moltbot-2026',
    title: 'OpenClaw: From "Clawd" to 180K Stars - The AI Agent That Changed Names Twice',
    date: '2026-01-29',
    displayDate: 'November 2025 – February 2026',
    category: NEWS_CATEGORIES.OPEN_SOURCE,
    tags: ['OpenClaw', 'Clawd', 'Moltbot', 'AI Agent', 'Open Source', 'Anthropic'],
    excerpt:
      'Started as "Clawd," renamed "Moltbot" after Anthropic\'s trademark team objected, and settled as "OpenClaw" - this self-hosted AI agent reached 180,000+ GitHub stars before its creator joined OpenAI.',
    sources: [
      { label: 'OpenClaw Blog', url: 'https://www.molt.bot/blog/introducing-openclaw' },
      { label: 'OpenClaw History', url: 'https://openclawroadmap.com/about-history.php' },
      { label: '36Kr (EN)', url: 'https://eu.36kr.com/en/p/3747666107036166' },
    ],
  },
  {
    slug: 'lobsterai-netease-youdao-2026',
    title: 'LobsterAI (有道龙虾): China\'s Open-Source Local-First AI Agent',
    date: '2026-02-11',
    displayDate: 'February 11, 2026',
    category: NEWS_CATEGORIES.OPEN_SOURCE,
    tags: ['LobsterAI', 'NetEase', 'Youdao', 'China', 'AI Agent', 'Local-first', 'MIT'],
    excerpt:
      'NetEase Youdao\'s LobsterAI (养龙虾) is China\'s first fully open-source AI agent - handling Excel reports, PPT generation, browser automation, and video creation entirely on-device with zero cloud data exposure.',
    sources: [
      { label: 'GitHub', url: 'https://github.com/netease-youdao/lobsterai' },
      { label: 'Tencent Cloud Dev', url: 'https://cloud.tencent.com/developer/article/2639502' },
      { label: 'Tencent News', url: 'https://view.inews.qq.com/a/20260311A08CDB00' },
    ],
  },
  {
    slug: 'nvidia-vera-rubin-2026',
    title: 'NVIDIA Vera Rubin: 3.6 Exaflops in a Single Rack',
    date: '2026-01-06',
    displayDate: 'CES 2026 (Jan 6, 2026)',
    category: NEWS_CATEGORIES.HARDWARE,
    tags: ['NVIDIA', 'Vera Rubin', 'GPU', 'HBM4', 'Exascale', 'Agentic AI'],
    excerpt:
      'Announced at CES 2026, NVIDIA\'s Vera Rubin NVL72 rack delivers 3.6 exaflops on 72 GPUs with 288 GB HBM4 each - 5× Blackwell performance at 10× lower cost per token. Shipping to hyperscalers in H2 2026.',
    sources: [
      { label: 'NVIDIA Vera Rubin NVL72', url: 'https://www.nvidia.com/en-us/data-center/vera-rubin-nvl72/' },
      { label: 'Hashrate Index', url: 'https://hashrateindex.com/blog/nvidia-vera-rubin-nvl72-specs-breakdown/' },
      { label: 'NVIDIA Dev Blog', url: 'https://developer.nvidia.com/blog/inside-the-nvidia-rubin-platform-six-new-chips-one-ai-supercomputer' },
    ],
  },
  {
    slug: 'amd-instinct-mi350-supercomputer',
    title: 'AMD Instinct MI350–MI400 & the Lux Sovereign AI Supercomputer',
    date: '2025-10-27',
    displayDate: 'October 2025 – 2026',
    category: NEWS_CATEGORIES.HARDWARE,
    tags: ['AMD', 'Instinct', 'MI350X', 'MI400', 'EPYC Turin', 'Supercomputer', 'Sovereign AI'],
    excerpt:
      'AMD\'s MI355X rack delivers 2.6 exaflops on 128 GPUs, the Lux AI supercomputer at ORNL is a $1B US sovereign AI bet, and the MI400 targets 40 petaflops per GPU with HBM4 in 2026.',
    sources: [
      { label: 'AMD Blog', url: 'https://www.amd.com/en/blogs/2025/amd-instinct-mi350-series-and-beyond-accelerating-the-future-of-ai-and-hpc.html' },
      { label: 'AMD Press Release', url: 'https://www.amd.com/en/newsroom/press-releases/2025-10-27-amd-powers-u-s-sovereign-ai-factory-supercomputer.html' },
      { label: 'ORNL', url: 'https://www.olcf.ornl.gov/2025/10/27/ornl-amd-and-hpe-to-deliver-does-newest-ai-supercomputers-discovery-and-lux/' },
      { label: 'WCCFTech', url: 'https://wccftech.com/amd-details-plans-for-instinct-ai-gpu-lineup-mi350-to-now-release-by-mid-2025-mi400-lineup-slated-for-2026/' },
    ],
  },
  {
    slug: 'nano-banana-google-image-2026',
    title: 'Nano Banana: Google\'s Viral AI Image Model Goes Pro',
    date: '2026-02-26',
    displayDate: 'February 26, 2026',
    category: NEWS_CATEGORIES.RESEARCH,
    tags: ['Google', 'Nano Banana', 'Image Generation', 'Gemini', 'Multimodal'],
    excerpt:
      'Google\'s Nano Banana (Gemini 3.1 Flash Image) generated 5 billion images in its first two months. The v2 update delivers Pro-level quality at Flash speed with internet-grounded accuracy.',
    sources: [
      { label: 'Google Blog', url: 'https://blog.google/innovation-and-ai/technology/ai/nano-banana-2' },
      { label: 'Ars Technica', url: 'https://arstechnica.com/ai/2026/02/google-releases-nano-banana-2-ai-image-generator-promises-pro-results-with-flash-speed/' },
      { label: 'Reuters', url: 'http://www.reuters.com/world/asia-pacific/google-rolls-out-nano-banana-2-after-viral-success-ai-image-generation-tool-2026-02-26/' },
    ],
  },
  {
    slug: 'gemini-3-launch-2025',
    title: 'Google Gemini 3: Reasoning, Agents, and Deep Think',
    date: '2025-11-18',
    displayDate: 'November 18, 2025',
    category: NEWS_CATEGORIES.MILESTONE,
    tags: ['Google', 'Gemini', 'Reasoning', 'Multimodal', 'Agentic AI'],
    excerpt:
      'Google Gemini 3 unifies multimodality, long context, and native tool use in one model family. The February 2026 Deep Think update adds a science-grade reasoning mode for complex, incomplete-data problems.',
    sources: [
      { label: 'Google Blog', url: 'http://blog.google/products/gemini/gemini-3/' },
      { label: 'Gemini 3 Deep Think', url: 'https://blog.google/innovation-and-ai/models-and-research/gemini-models/gemini-3-deep-think/' },
      { label: 'Google Cloud Blog', url: 'https://cloud.google.com/blog/products/ai-machine-learning/expanding-gemini-2-5-flash-and-pro-capabilities' },
    ],
  },
  {
    slug: 'ilmu-malaysia-ai-2025',
    title: 'ILMU: Malaysia\'s First Homegrown Multimodal AI',
    date: '2025-09-16',
    displayDate: 'September 16, 2025',
    category: NEWS_CATEGORIES.MILESTONE,
    tags: ['Malaysia', 'ILMU', 'YTL', 'Bahasa Melayu', 'Sovereign AI'],
    excerpt:
      'YTL AI Labs launched ILMU on Malaysia Day 2025 - a multimodal AI fluent in Bahasa Melayu, Manglish, and local dialects, outperforming GPT-4o on Malay benchmarks, hosted entirely in Malaysia.',
    sources: [
      { label: 'YTL AI Labs', url: 'https://www.ytlailabs.com/' },
      { label: 'Nasi Lemak Tech', url: 'https://nasilemaktech.com/malaysia-launches-ilmu-first-multimodal-ai-model/' },
      { label: 'Ringgit Plus', url: 'https://ringgitplus.com/en/blog/technology/ytl-power-launches-ilmu-malaysias-first-homegrown-large-language-model.html' },
      { label: 'Malaysiakini', url: 'https://www.malaysiakini.com/announcement/752379' },
    ],
  },
  {
    slug: 'mistral-ai-open-source',
    title: 'Mistral AI: Europe\'s Open-Source Frontier Lab',
    date: '2025-03-16',
    displayDate: 'March 2025 – March 2026',
    category: NEWS_CATEGORIES.OPEN_SOURCE,
    tags: ['Mistral', 'Open Source', 'France', 'MoE', 'Apache 2.0'],
    excerpt:
      'From Mixtral\'s 2023 MoE breakthrough to Mistral Small 4\'s unified 119B multimodal model in March 2026, France\'s Mistral AI has become the benchmark for efficient, open-weight frontier AI.',
    sources: [
      { label: 'Mistral Changelog', url: 'https://docs.mistral.ai/getting-started/changelog/' },
      { label: 'MarkTechPost', url: 'https://www.marktechpost.com/2026/03/16/mistral-ai-releases-mistral-small-4-a-119b-parameter-moe-model-that-unifies-instruct-reasoning-and-multimodal-workloads/' },
      { label: 'Wikipedia', url: 'https://en.wikipedia.org/wiki/Mistral_AI' },
    ],
  },
  {
    slug: 'google-turboquant-kv-cache-2026',
    title: 'Google TurboQuant: KV Cache Compression Rattles Memory Chip Stocks',
    date: '2026-03-26',
    displayDate: 'March 26, 2026',
    category: NEWS_CATEGORIES.RESEARCH,
    tags: ['Google', 'KV Cache', 'Memory', 'Samsung', 'SK Hynix'],
    excerpt:
      "Google's TurboQuant algorithm compresses AI inference KV cache to 1/6th its size with near-zero accuracy loss - triggering a 6%+ sell-off in Samsung and SK Hynix shares.",
    sources: [
      { label: 'Korea Economic Daily', url: 'https://www.kedglobal.com/stocks/newsView/ked202603260003' },
      { label: 'Bloomberg', url: 'https://www.bloomberg.com/news/articles/2026-03-26/memory-stock-boom-seen-resilient-to-threat-from-new-google-tech' },
      { label: 'SE Daily', url: 'https://en.sedaily.com/technology/2026/03/28/googles-turboquant-compresses-kv-cache-to-one-sixth' },
    ],
  },
  {
    slug: 'minimax-m27-2026',
    title: 'MiniMax M2.7: The Self-Evolving AI Model',
    date: '2026-03-18',
    displayDate: 'March 18, 2026',
    category: NEWS_CATEGORIES.RESEARCH,
    tags: ['MiniMax', 'Open Source', 'Agentic AI'],
    excerpt:
      'MiniMax M2.7 introduces recursive self-improvement capabilities, autonomously handling 30–50% of RL research workflows and outperforming Claude Sonnet in tool-calling benchmarks.',
    sources: [
      { label: 'VentureBeat', url: 'https://venturebeat.com/technology/new-minimax-m2-7-proprietary-ai-model-is-self-evolving-and-can-perform-30-50' },
      { label: 'MiniMax API Docs', url: 'https://platform.minimax.io/docs/release-notes/models' },
    ],
  },
  {
    slug: 'nvidia-dgx-spark-2025',
    title: 'NVIDIA DGX Spark: A Supercomputer on Your Desk',
    date: '2025-10-01',
    displayDate: 'October 2025',
    category: NEWS_CATEGORIES.HARDWARE,
    tags: ['NVIDIA', 'DGX Spark', 'Hardware', 'Blackwell'],
    excerpt:
      "NVIDIA's DGX Spark brings 1 petaflop of FP4 AI compute and 128 GB unified memory to a desktop form factor - capable of running 200B-parameter models locally.",
    sources: [
      { label: 'NVIDIA Investor Relations', url: 'https://investor.nvidia.com/news/press-release-details/2025/NVIDIA-DGX-Spark-Arrives-for-Worlds-AI-Developers/default.aspx' },
      { label: 'NVIDIA DGX Spark', url: 'https://www.nvidia.com/dgx-spark/' },
      { label: 'The Register', url: 'https://www.theregister.com/2026/01/05/nvidia_dgx_spark_speed/' },
    ],
  },
  {
    slug: 'qwen-open-source-alibaba-2025',
    title: "Qwen: Alibaba's Open-Source AI Push",
    date: '2025-04-01',
    displayDate: 'April 2025 – March 2026',
    category: NEWS_CATEGORIES.OPEN_SOURCE,
    tags: ['Alibaba', 'Qwen', 'Open Source', 'Multimodal'],
    excerpt:
      'From Qwen3 to Qwen3.5, Alibaba has built one of the most comprehensive open-source AI families - supporting 201 languages, hybrid reasoning, and multimodal capabilities up to 397B parameters.',
    sources: [
      { label: 'Alibaba Group', url: 'https://www.alibabagroup.com/document-1960233590314762240' },
      { label: 'Alibaba Group (Qwen3)', url: 'https://www.alibabagroup.com/en-US/document-1853940226976645120' },
      { label: 'GitHub Qwen3.5', url: 'https://github.com/QwenLM/Qwen3.5' },
    ],
  },
  {
    slug: 'grok-4-xai-2025',
    title: 'Grok 3 & 4: xAI Enters the Reasoning Arena',
    date: '2025-07-09',
    displayDate: 'Feb 2025 – Jul 2025',
    category: NEWS_CATEGORIES.MILESTONE,
    tags: ['xAI', 'Grok', 'Reasoning', 'Elon Musk'],
    excerpt:
      'xAI launched Grok 3 in February 2025, trained on 200,000 H100 GPUs, then Grok 4 in July 2025 - the first model to exceed 50% on the Humanity\'s Last Exam (HLE) benchmark.',
    sources: [
      { label: 'TechCrunch (Grok 3)', url: 'https://techcrunch.com/2025/02/17/elon-musks-ai-company-xai-releases-its-latest-flagship-ai-grok-3' },
      { label: 'xAI Grok 3 Blog', url: 'https://x.ai/news/grok-3' },
      { label: 'xAI Grok 4', url: 'https://x.ai/news/grok-4' },
    ],
  },
  {
    slug: 'anthropic-open-claude-oss',
    title: 'Anthropic Opens Claude to the Open-Source Community',
    date: '2025-06-01',
    displayDate: 'June 2025',
    category: NEWS_CATEGORIES.OPEN_SOURCE,
    tags: ['Anthropic', 'Claude', 'Open Source', 'Developers'],
    excerpt:
      "Anthropic launched the \"Claude for Open Source\" program, offering eligible OSS maintainers free Claude Max 20x access - bridging frontier AI with the open-source ecosystem.",
    sources: [
      { label: 'Anthropic', url: 'https://www.anthropic.com/claude-for-oss-terms' },
      { label: 'TechCrunch', url: 'https://techcrunch.com/2025/02/25/claude-everything-you-need-to-know-about-anthropics-ai' },
    ],
  },
  {
    slug: 'rdma-cluster-ai-networking',
    title: 'RDMA & InfiniBand: The Invisible Backbone of AI Clusters',
    date: '2025-03-01',
    displayDate: 'March 2025',
    category: NEWS_CATEGORIES.HARDWARE,
    tags: ['RDMA', 'InfiniBand', 'Networking', 'GPU Cluster', 'Infrastructure'],
    excerpt:
      'As AI clusters scale to hundreds of thousands of GPUs, RDMA networking over InfiniBand has become the critical fabric enabling zero-copy, low-latency synchronisation across nodes.',
    sources: [
      { label: 'Rack2Cloud Architecture', url: 'https://www.rack2cloud.com/architecture/ai-infrastructure/distributed-ai-fabrics' },
      { label: 'IBM Research 2026', url: 'https://research.ibm.com/publications/designing-roce-networks-for-ai-workloads' },
      { label: 'Network DNA Blog', url: 'https://www.thenetworkdna.com/2026/03/ai-data-center-networking-how-gpu.html' },
    ],
  },
  {
    slug: 'singapore-ai-governance-scam',
    title: "Singapore's AI-Powered War on Scams",
    date: '2025-01-15',
    displayDate: 'January 2025 (ongoing)',
    category: NEWS_CATEGORIES.GOVERNANCE,
    tags: ['Singapore', 'AI Governance', 'Anti-Scam', 'Policy'],
    excerpt:
      "Singapore's government is deploying AI-based solutions through its Anti-Scam Command (ASCom) to combat deepfake fraud and social engineering - recovering over $56.7 million in H1 2025 alone.",
    sources: [
      { label: 'Bank Info Security', url: 'https://www.bankinfosecurity.asia/singapore-to-target-scammers-ai-punish-them-by-caning-a-27680' },
      { label: 'CybersecurityAsia', url: 'https://cybersecurityasia.net/ai-driven-scams-tighten-grip-on-singapore/' },
      { label: 'SPF Advisory', url: 'https://www.police.gov.sg/media-hub/news/2025/03/20250312_joint_advisory_on_scams_involving_digital_manipulation' },
    ],
  },
  {
    slug: 'deepseek-moment-2025',
    title: "The DeepSeek Moment: AI's Sputnik Wake-Up Call",
    date: '2025-01-20',
    displayDate: 'January 20, 2025',
    category: NEWS_CATEGORIES.MILESTONE,
    tags: ['DeepSeek', 'China', 'NVIDIA', 'Open Source', 'Cost Efficiency'],
    excerpt:
      "DeepSeek R1 launched on Jan 20, 2025, matching OpenAI o1 performance at a reported training cost of $5.6M - wiping $600 billion from NVIDIA's market cap in a single day.",
    sources: [
      { label: 'The Verge', url: 'https://on.theverge.com/24353060/deepseek-ai-china-nvidia-openai' },
      { label: 'Enabifly Analysis', url: 'https://enabifly.com/2025/01/28/the-rise-of-deepseek-the-sputnik-moment-of-ai/' },
      { label: 'Spyglass', url: 'https://spyglass.org/deepseek-moment' },
    ],
  },
  {
    slug: 'chatgpt-launch-2022',
    title: 'ChatGPT Launch: The Day AI Went Mainstream',
    date: '2022-11-30',
    displayDate: 'November 30, 2022',
    category: NEWS_CATEGORIES.MILESTONE,
    tags: ['OpenAI', 'ChatGPT', 'GPT-3.5', 'History'],
    excerpt:
      'OpenAI launched ChatGPT on November 30, 2022, as a "low-key research preview." Servers crashed within hours. Within two years it had 300 million weekly users and $4B in annual revenue.',
    sources: [
      { label: 'OpenAI Blog', url: 'https://openai.com/blog/chatGPT' },
      { label: 'The Verge (2-year anniversary)', url: 'https://www.theverge.com/2024/12/12/24318650/chatgpt-openai-history-two-year-anniversary' },
      { label: 'Dev Help Center', url: 'https://devhelpcenter.com/when-did-chatgpt-launch/' },
    ],
  },
];

/** Sort articles by date descending (newest first) */
export const SORTED_ARTICLES = [...NEWS_ARTICLES].sort(
  (a, b) => new Date(b.date) - new Date(a.date)
);
